import { Injectable, inject } from '@angular/core';
import {ELoginStatus} from '@app/auth-config/models/auth-state.model';
import {selectTokenStatus} from '@app/auth-config/state/auth.selector';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {ILanguage} from '@app/shared/models/language.model';
import {LanguageService} from '@app/shared/services/language.service';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, map, of, switchMap, withLatestFrom} from 'rxjs';
import {
  getLanguagesFailure,
  getLanguagesSuccess,
  LanguageActions,
  setLanguageFromLanguageEffect,
  setPreferredLanguage,
  setPreferredLanguageFailure,
  setPreferredLanguageSuccess,
} from './language.action';

@Injectable()
export class LanguageEffects {
  private readonly actions$ = inject(Actions);
  private readonly languageService = inject(LanguageService);
  private readonly store = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);

  globalToast = 'global-toast';

  getLanguages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.GetLanguages),
      switchMap(() => this.getLanguages())
    )
  );

  setPreferredLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguageActions.SetPreferredLanguage),
      withLatestFrom(this.store.select(selectTokenStatus)),
      switchMap(([action, state]) => {
        if (state == ELoginStatus.LoggedIn) {
          return this.setPreferredLanguage(action);
        }
        return of(setPreferredLanguageSuccess());
      })
    )
  );

  private setPreferredLanguage(action: ReturnType<typeof setPreferredLanguage>) {
    return this.languageService.setPreferredLanguage(action.languageId).pipe(
      map(() => {
        this.localStorageService.setItem(StorageKeys.currentLanguage, action.languageId);
        this.store.dispatch(setLanguageFromLanguageEffect({languageId: action.languageId}));
        return setPreferredLanguageSuccess();
      }),
      catchError(() => of(setPreferredLanguageFailure()))
    );
  }

  private getLanguages() {
    const cachedLanguages = this.localStorageService.getItem<ILanguage[]>(StorageKeys.languages);
    if (cachedLanguages) {
      return of(getLanguagesSuccess({languages: cachedLanguages}));
    }
    return this.languageService.getLanguages().pipe(
      map(languages => {
        this.localStorageService.setItem<ILanguage[]>(StorageKeys.languages, languages);
        return getLanguagesSuccess({languages: languages});
      }),
      catchError(() => of(getLanguagesFailure()))
    );
  }
}
